import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useStateManager } from './useStateManager';
import html2canvas from 'html2canvas';
import { supabase } from '@/integrations/supabase/client';

export const useChatState = () => {
  const { t } = useLanguage();
  const { state, updateChatState } = useStateManager();
  
  // Local input state (not persisted)
  const [input, setInput] = useState('');
  
  // SMS Screenshot modal state
  const [showSMSScreenshotModal, setShowSMSScreenshotModal] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState('');
  
  // Get state from manager
  const isExpanded = state.isExpanded;
  const showNewConversationDialog = state.showNewConversationDialog;
  
  // Update functions
  const setIsExpanded = useCallback((expanded: boolean) => {
    updateChatState({ isExpanded: expanded });
  }, [updateChatState]);
  
  const setShowNewConversationDialog = useCallback((show: boolean) => {
    updateChatState({ showNewConversationDialog: show });
  }, [updateChatState]);

  const handleNewConversationClick = useCallback(() => {
    setShowNewConversationDialog(true);
  }, []);

  const handleCopyConversation = useCallback(async (messages: any[], messagesContainerRef?: React.RefObject<HTMLElement>) => {
    if (!messagesContainerRef?.current) {
      toast.error('Unable to capture conversation');
      return;
    }

    const sessionId = `session-${Date.now()}`;
    
    // Find the chat window container
    const chatContainer = messagesContainerRef.current.closest('.d2cbot-chat-container') || messagesContainerRef.current.parentElement;
    if (!chatContainer) {
      toast.error('Unable to find chat container');
      return;
    }
    
    // Create loading overlay scoped to chat window
    const overlay = document.createElement('div');
    overlay.id = 'copy-conversation-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      gap: 16px;
      border-radius: inherit;
    `;
    
    const loadingText = t('copyConversation.loading');
    overlay.innerHTML = `
      <div style="
        width: 48px;
        height: 48px;
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-top-color: white;
        border-radius: 50%;
        animation: d2cbot-overlay-spin 1s linear infinite;
      "></div>
      <div style="color: white; font-size: 16px; text-align: center; font-family: system-ui, sans-serif;">
        ${loadingText}
      </div>
      <style>
        @keyframes d2cbot-overlay-spin {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    
    // Ensure parent has position for absolute overlay
    const originalPosition = (chatContainer as HTMLElement).style.position;
    if (!originalPosition || originalPosition === 'static') {
      (chatContainer as HTMLElement).style.position = 'relative';
    }
    chatContainer.appendChild(overlay);

    try {
      
      // Get the ScrollArea viewport element
      const scrollAreaViewport = messagesContainerRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      const contentContainer = scrollAreaViewport?.firstElementChild as HTMLElement;
      
      if (!contentContainer) {
        overlay.remove();
        if (originalPosition) {
          (chatContainer as HTMLElement).style.position = originalPosition;
        }
        toast.error('Unable to find conversation content');
        return;
      }

      // Find all images in the conversation
      const images = contentContainer.querySelectorAll('img');
      const imageUrls = Array.from(images)
        .map(img => img.src)
        .filter(src => src && !src.startsWith('data:')); // Exclude data URLs

      // Proxy images through Supabase if there are any
      let imageMapping: { original: string; proxied: string }[] = [];
      if (imageUrls.length > 0) {
        const { data: proxyData, error: proxyError } = await supabase.functions.invoke(
          'proxy-conversation-images',
          {
            body: {
              action: 'proxy',
              sessionId,
              imageUrls,
            },
          }
        );

        if (proxyError || !proxyData?.success) {
          console.error('Error proxying images:', proxyError);
          toast.warning('Some images may not appear in the capture');
        } else {
          imageMapping = proxyData.proxiedUrls;
        }
      }

      // Store original styles
      const originalViewportStyle = {
        height: scrollAreaViewport.style.height,
        maxHeight: scrollAreaViewport.style.maxHeight,
        overflow: scrollAreaViewport.style.overflow,
        position: scrollAreaViewport.style.position,
      };
      const originalScrollTop = scrollAreaViewport.scrollTop;

      // Replace image URLs with proxied versions
      const originalImageSrcs: { img: HTMLImageElement; src: string }[] = [];
      imageMapping.forEach(({ original, proxied }) => {
        const img = Array.from(images).find(img => img.src === original);
        if (img) {
          originalImageSrcs.push({ img, src: img.src });
          img.src = proxied;
        }
      });

      // Expand viewport to show all content
      scrollAreaViewport.style.height = 'auto';
      scrollAreaViewport.style.maxHeight = 'none';
      scrollAreaViewport.style.overflow = 'visible';
      scrollAreaViewport.style.position = 'relative';
      scrollAreaViewport.scrollTop = 0;

      // Wait for images to load and layout to settle
      await new Promise(resolve => setTimeout(resolve, 500));

      toast.info('Capturing conversation...');

      // Capture the full content
      const canvas = await html2canvas(contentContainer, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
        width: contentContainer.scrollWidth,
        height: contentContainer.scrollHeight,
        windowWidth: contentContainer.scrollWidth,
        windowHeight: contentContainer.scrollHeight,
      });

      // Restore original styles and image sources
      scrollAreaViewport.style.height = originalViewportStyle.height;
      scrollAreaViewport.style.maxHeight = originalViewportStyle.maxHeight;
      scrollAreaViewport.style.overflow = originalViewportStyle.overflow;
      scrollAreaViewport.style.position = originalViewportStyle.position;
      scrollAreaViewport.scrollTop = originalScrollTop;

      originalImageSrcs.forEach(({ img, src }) => {
        img.src = src;
      });

      // Cleanup temporary storage
      if (imageUrls.length > 0) {
        await supabase.functions.invoke('proxy-conversation-images', {
          body: { action: 'cleanup', sessionId },
        });
      }

      // Convert canvas to blob
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });

      if (!blob) {
        toast.error('Failed to create image');
        overlay.remove();
        if (originalPosition) {
          (chatContainer as HTMLElement).style.position = originalPosition;
        }
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `conversation-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      
      overlay.remove();
      if (originalPosition) {
        (chatContainer as HTMLElement).style.position = originalPosition;
      }
      
      toast.success('Conversation downloaded');
    } catch (error) {
      console.error('Error capturing conversation:', error);
      overlay.remove();
      if (chatContainer && originalPosition) {
        (chatContainer as HTMLElement).style.position = originalPosition;
      }
      toast.error('Failed to capture conversation');
    }
  }, [t]);

  return {
    // State
    input,
    setInput,
    isExpanded,
    setIsExpanded,
    showNewConversationDialog,
    setShowNewConversationDialog,
    showSMSScreenshotModal,
    setShowSMSScreenshotModal,
    screenshotUrl,
    
    // Handlers
    handleNewConversationClick,
    handleCopyConversation
  };
};